import React, {useState, useEffect} from 'react';
import {useParams, useNavigate} from 'react-router-dom';
import {
    Tabs,
    Card,
    Typography,
    Button,
    Spin,
    App,
    Row,
    Col,
    Steps,
    Space
} from 'antd';
import {authService, fiscalCashRegisterService} from '../../services/api';
import {usePageTitle} from '../../context/PageTitleContext';
import ErrorPage from "../ErrorPage/ErrorPage";
import HorizontalViewItem from "../../components/HorizontalItems/ViewItem";

// Функция для форматирования даты
const formatDate = (dateString) => {
    if (!dateString) return 'Не указано';
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${day}.${month}.${year} в ${hours}:${minutes}`;
};

const {Title, Text} = Typography;

const FiscalCashRegisterPage = () => {
    const {id} = useParams();
    const navigate = useNavigate();
    const {message} = App.useApp();
    const {setPageTitle, setRightContent} = usePageTitle();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [fiscalCashRegister, setFiscalCashRegister] = useState(null);
    const [historyData, setHistoryData] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyLoaded, setHistoryLoaded] = useState(false);
    const [formData, setFormData] = useState({
        types: []
    });

    useEffect(() => {
        fetchData();
        setPageTitle('Просмотр фискальной кассы');

        setRightContent(
            authService.hasPermission('pos:fiscal_cash_registers:edit') && (
                <Button
                    type="primary"
                    onClick={() => navigate(`/fiscal-cash-registers/${id}/edit`)}
                >
                    Редактировать
                </Button>
            )
        );

        return () => {
            setPageTitle('');
            setRightContent(null);
            setFiscalCashRegister(null);
        };
    }, [setPageTitle, setRightContent, id, navigate]);

    const fetchData = async () => {
        try {
            setLoading(true);

            // Получаем данные фискальной кассы
            const fiscalCashRegisterResponse = await fiscalCashRegisterService.getFiscalCashRegisterById(id);

            // Получаем данные для формы
            const formDataResponse = await fiscalCashRegisterService.getFormData();

            if (fiscalCashRegisterResponse.status === 'success' && formDataResponse.status === 'success') {
                setFiscalCashRegister(fiscalCashRegisterResponse.data);
                setFormData(formDataResponse.data);
                console.log('Получены данные фискальной кассы:', fiscalCashRegisterResponse.data);
            } else {
                message.error('Не удалось загрузить данные фискальной кассы');
                navigate('/fiscal-cash-registers');
            }
        } catch (error) {
            if (error.response?.status === 404) {
                setError({
                    statusCode: 404,
                    title: "Фискальная касса не найдена",
                    description: "Указанная фискальная касса не существует или была удалена."
                });
            } else {
                setError({
                    statusCode: error.response?.status || 500,
                    title: "Ошибка загрузки данных",
                    description: "Произошла ошибка при загрузке данных фискальной кассы. Попробуйте обновить страницу или вернуться позже."
                });
            }
        } finally {
            setLoading(false);
        }
    };

    const fetchHistory = async () => {
        try {
            setHistoryLoading(true);
            const response = await fiscalCashRegisterService.getFiscalCashRegisterHistory(id);

            if (response.status === 'success') {
                setHistoryData(response.data);
            } else {
                message.error('Не удалось загрузить историю изменений');
            }
        } catch (error) {
            message.error(error.message || 'Ошибка при загрузке истории изменений');
        } finally {
            setHistoryLoading(false);
            setHistoryLoaded(true);
        }
    };

    // Функция для получения лейбла из списка типов
    const getTypeLabel = (value) => {
        if (!value || !formData.types) return 'Не указано';
        const type = formData.types.find(t => t.value === value || t.id === value);
        return type ? (type.label || type.name) : value;
    };

    if (loading) {
        return (
            <div style={{textAlign: 'center', padding: '50px 0'}}>
                <Spin size="large"/>
            </div>
        );
    }

    if (error) {
        return <ErrorPage
            statusCode={error.statusCode}
            title={error.title}
            description={error.description}
        />;
    }

    return (
        <Card>
            <Title level={3} style={{marginTop: 0}}>
                Фискальная касса «{fiscalCashRegister.name}»
            </Title>
            <Space>
                <Text>ID: <Text>{fiscalCashRegister.id}</Text></Text>
            </Space>

            <Tabs defaultActiveKey="data" items={[
                {
                    key: "data",
                    label: "Данные",
                    children: (
                        <Row gutter={24}>
                            <Col span={8}>
                                <Space direction="vertical" size={16} style={{ marginBottom: 38, display: 'block' }}>
                                    <Title level={5} style={{ marginBottom: 20 }}>Основные атрибуты</Title>
                                    <HorizontalViewItem label="Название">
                                        <Text>{fiscalCashRegister.name || 'Не указано'}</Text>
                                    </HorizontalViewItem>

                                    <HorizontalViewItem label="Внешний ID">
                                        <Text>{fiscalCashRegister.external_id || 'Не указано'}</Text>
                                    </HorizontalViewItem>

                                    <HorizontalViewItem label="Тип">
                                        <Text>{getTypeLabel(fiscalCashRegister.type)}</Text>
                                    </HorizontalViewItem>

                                    <HorizontalViewItem label="Серийный номер ККМ">
                                        <Text>{fiscalCashRegister.kkm_serial_number || 'Не указано'}</Text>
                                    </HorizontalViewItem>

                                    <HorizontalViewItem label="Налоговый номер ККМ">
                                        <Text>{fiscalCashRegister.kkm_tax_register_number || 'Не указано'}</Text>
                                    </HorizontalViewItem>
                                </Space>

                                <Space direction="vertical" size={16} style={{ marginBottom: 38, display: 'block' }}>
                                    <Title level={5} style={{ marginBottom: 20 }}>Интеграция</Title>
                                    <HorizontalViewItem label="Логин интеграции">
                                        <Text>{fiscalCashRegister.integration_login || 'Не указано'}</Text>
                                    </HorizontalViewItem>

                                    <HorizontalViewItem label="Пароль интеграции">
                                        <Text>{fiscalCashRegister.integration_password ? '••••••••' : 'Не указано'}</Text>
                                    </HorizontalViewItem>
                                </Space>
                            </Col>

                            <Col span={8}>
                                <Space direction="vertical" size={16} style={{ marginBottom: 38, display: 'block' }}>
                                    <Title level={5} style={{ marginBottom: 20 }}>Данные для чека</Title>
                                    <HorizontalViewItem label="Наименование ОФД">
                                        <Text>{fiscalCashRegister.ofd_name || 'Не указано'}</Text>
                                    </HorizontalViewItem>

                                    <HorizontalViewItem label="Сайт ОФД">
                                        <Text>{fiscalCashRegister.ofd_site || 'Не указано'}</Text>
                                    </HorizontalViewItem>

                                    <HorizontalViewItem label="Наименование компании">
                                        <Text>{fiscalCashRegister.company_name || 'Не указано'}</Text>
                                    </HorizontalViewItem>

                                    <HorizontalViewItem label="Налоговый номер компании">
                                        <Text>{fiscalCashRegister.company_tax_id || 'Не указано'}</Text>
                                    </HorizontalViewItem>

                                    <HorizontalViewItem label="Адрес компании">
                                        <Text>{fiscalCashRegister.company_address || 'Не указано'}</Text>
                                    </HorizontalViewItem>
                                </Space>
                            </Col>

                            <Col span={8}>
                                <Space direction="vertical" size={16} style={{ marginBottom: 38, display: 'block' }}>
                                    <Title level={5} style={{ marginBottom: 20 }}>Системная информация</Title>
                                    <HorizontalViewItem label="Дата создания">
                                        <Text>{formatDate(fiscalCashRegister.created_at)}</Text>
                                    </HorizontalViewItem>

                                    <HorizontalViewItem label="Дата обновления">
                                        <Text>{formatDate(fiscalCashRegister.updated_at)}</Text>
                                    </HorizontalViewItem>

                                    <HorizontalViewItem label="Дата обновления токена">
                                        <Text>{formatDate(fiscalCashRegister.integration_token_create_date)}</Text>
                                    </HorizontalViewItem>
                                </Space>
                            </Col>
                        </Row>
                    )
                },
                {
                    key: "history",
                    label: "История изменений",
                    children: (
                        <Card>
                            {historyLoading ? (
                                <div style={{textAlign: 'center', padding: '50px 0'}}>
                                    <Spin size="large"/>
                                </div>
                            ) : (
                                <Steps
                                    direction="vertical"
                                    size="small"
                                    current={historyData.length}
                                    progressDot
                                    items={historyData.map(item => ({
                                        title: item.change_description,
                                        description: `${item.full_name} (${item.username}), ${formatDate(item.created_at)}`
                                    }))}
                                />
                            )}
                        </Card>
                    )
                }
            ]}
                  onChange={(key) => {
                      if (key === 'history' && !historyLoaded) {
                          fetchHistory();
                      }
                  }}/>
        </Card>
    );
}

export default FiscalCashRegisterPage;